'use client'

import { useState } from 'react'

import Image from '@/components/image'
import type { ImageSizingProps } from '@/components/image/types'
import { Skeleton } from '@/components/server/shared/skeleton'
import { lcpImageSource } from '@/lib/images/lcp-optimizer'

import { useYoutubeThumbnail } from './use-youtube-thumbnail'

interface YoutubeThumbnailProps {
  videoId: string
  thumbnail?: string
  title: string
  eager: boolean
  delivery?: 'same-origin'
  sizing?: ImageSizingProps
}

export const YoutubeThumbnail = ({ videoId, thumbnail, title, eager, delivery, sizing }: YoutubeThumbnailProps) => {
  const { url, ref, loaded, onLoad, onError } = useYoutubeThumbnail(videoId, thumbnail)
  const [directSource, setDirectSource] = useState<string>()
  const sameOrigin = delivery === 'same-origin' && directSource !== url && !!lcpImageSource(url)
  const storyblokAsset = url.startsWith('https://a.storyblok.com/f/')
  return (
    <>
      {eager && !url.startsWith('data:') && !storyblokAsset && !sameOrigin && (
        <link rel="preload" as="image" href={url} fetchPriority="high" />
      )}
      {!loaded && (
        <span className="absolute inset-0 flex" aria-hidden>
          <Skeleton as="span" />
        </span>
      )}
      <Image
        image={storyblokAsset ? { filename: url } : url}
        origin="youtube-thumbnail"
        loadTime={eager ? 'eager' : 'lazy'}
        delivery={sameOrigin ? 'same-origin' : undefined}
        sizingProps={storyblokAsset || sameOrigin ? sizing : undefined}
        imgProps={{
          ref,
          className: 'relative block h-full w-full rounded-3xl object-cover',
          width: 640,
          height: 360,
          alt: title,
          title,
          onLoad,
          onError: () => (sameOrigin ? setDirectSource(url) : onError()),
          style: { width: '100%', height: '100%' },
        }}
      />
    </>
  )
}
